/**
 * `/orders/:storeId/:orderId` — the order "job card" (roadmap Tier 2).
 *
 * One page per order: every garment joined to its gang sheet and screen-print press state, plus a
 * derived "production complete" signal. Closes the gap where an order, once batched, scattered across
 * the pick-list / press-run / board with no single place to track the whole thing.
 *
 * Fully client-side over endpoints that already exist — no new API. It reads `GET /orders/:s/:id`
 * (header + lines + rollup), for each sheet a line points at `GET /batches/:batchId` (status +
 * per-placement press state), and `GET /orders/:s/:id/reprints`. The batch queries share react-query's
 * `['batch', id]` cache with the Gang-Sheet page. "Production complete" is derived, never persisted —
 * bagging is a downstream shipping step.
 *
 * ⚠️ Every ship-facing affordance keys on `shipVerdict` (order-detail-lib.ts) — the strip at the top of
 * this page and the `orderHeadline` badge beside the order number. It is derived from the GARMENTS and
 * folds in the reprint lookup; never key one on `production.status`, which cannot see a reprint and
 * which the server derives with a hole (decision D4 / §8.25). FSP has no ship action to refuse, so the
 * whole "hard block" is that this page never presents the order as ready when it is not.
 */
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearch } from '@tanstack/react-router';
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CheckCircle2,
  Loader2,
  PackageCheck,
  PackageX,
  Printer,
  RefreshCw,
  RotateCcw,
} from 'lucide-react';
import { useId, useMemo, useRef, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useApiClient } from '@/lib/api/hooks';
import type { components } from '@/lib/api/types.gen';
import { useAuth } from '@/lib/auth-state';
import { cn } from '@/lib/utils';
import { withImageWidth } from './catalog-lib';
import { invalidateInventory } from './inventory-lib';
import {
  type BatchDetail,
  buildJobCard,
  canMarkPrintedOffSystem,
  formatStamp,
  GARMENT_TONE_CLASS,
  type GarmentPlacement,
  garmentIdleNote,
  garmentQtyLabel,
  garmentSizeChip,
  garmentStage,
  garmentStamp,
  groupGarmentsByLane,
  type JobCardGarment,
  laneCountLabel,
  OFF_SYSTEM_MARK_COST,
  orderBackTarget,
  orderHeadline,
  pressCheckboxLabel,
  type ReprintRef,
  reprintsByLine,
  type ShippedUndo,
  type ShipVerdict,
  shipOnlyPack,
  shippedUndo,
  shipVerdict,
  stockIdentityLine,
} from './order-detail-lib';
import { OrderHistorySection } from './order-history-section';
import {
  ALREADY_PRINTED_REASONS,
  type AlreadyPrintedReason,
  alreadyPrintedReasonLabel,
  dueStatus,
  relativeAge,
} from './orders-queue';
import { productionPieceLine } from './production-board-lib';
import { ResendDialog } from './resend-dialog';

export function OrderDetailPage() {
  // `from` is a route ID, not a URL: this page sits under the pathless `_shell` layout route
  // (router.tsx), so the ID carries that segment even though the address bar never does.
  const { storeId, orderId } = useParams({ from: '/_shell/orders/$storeId/$orderId' });
  /**
   * Where "back" goes. Ten screens link into this page and only one of them is the Orders queue, so a
   * hard-coded `/orders` was wrong for nine of them — and from `/production` it is worse than a wrong
   * label, because a batched line has already dropped off the open-work queue and the destination
   * provably does not contain the order the operator was just reading.
   *
   * `strict: false` + a cast is the house idiom for reading a search param off a route with no
   * `validateSearch` (orders-page.tsx:112, batches-page.tsx:103). The cast is safe because
   * `orderBackTarget` takes `unknown` and falls back for anything it does not recognise — the
   * validation lives in the pure fn, where an unknown origin must fall back rather than throw.
   */
  const { from } = useSearch({ strict: false }) as { from?: string };
  const backTo = orderBackTarget(from);
  const client = useApiClient();
  const { state: authState } = useAuth();
  /**
   * P7-5. Re-sending spends film, blanks and press time on garments the customer already paid for, so
   * `POST .../resend` requires a Cognito admin — the button is hidden for anyone else rather than
   * offering an action the API will 403. ⚠️ Both Cognito users currently hold `admin` (the floor login
   * too), so today this hides nothing; it becomes real when the groups are split.
   */
  const isManager = authState.status === 'signed-in' && authState.role.isAdmin;
  const [resendOpen, setResendOpen] = useState(false);
  const qc = useQueryClient();
  /**
   * Single-flight latch for the press check-off, in a REF rather than only `pressMut.isPending`.
   * `isPending` is state: it does not become true until React re-renders, so two taps landing inside
   * one tick both see `false` and both fire. A ref flips synchronously on the first one. Released in
   * the mutation's onSettled, so a failure re-arms it rather than wedging the row.
   */
  const pressInFlight = useRef(false);
  /**
   * The undo awaiting confirmation, or null. Held at page level rather than in the row so a refetch
   * that re-renders the list cannot dismiss a dialog the operator is reading.
   */
  const [confirmUndo, setConfirmUndo] = useState<{ g: JobCardGarment; undo: ShippedUndo } | null>(
    null,
  );
  /**
   * Single-flight latch for the confirmed undo, for the same reason as `pressInFlight`: two taps on
   * the dialog's confirm button inside one tick both read a stale `isPending`, and on two of these
   * three the second tap is a second ledger write.
   *
   * ⚠️ It holds the in-flight `kind:lineId`, NOT a boolean. A boolean latches the whole PAGE, and the
   * three undos are three separate mutations with three separate `isPending` flags — so while a pack
   * undo was in flight, confirming a cap's produce undo would have been swallowed with the dialog
   * closing and nothing happening: a silent no-op, which on a money control is the failure mode that
   * makes an operator repeat the action. Same-key taps are the race; different keys are different work.
   */
  const undoInFlight = useRef<string | null>(null);
  /** Base for the per-lane heading ids that name each lane's garment list. `useId` rather than a literal
   *  so two mounts of this page could never collide on a document id. */
  const laneIdBase = useId();
  /** Single-flight for the forward pack, which can draw a shelf item. Same reasoning as the others. */
  const packInFlight = useRef(false);
  /**
   * The garment whose off-system mark is being composed, or null (PR-6). Held at page level for the
   * same reason as `confirmUndo`: a change-feed refetch re-renders the whole garment list, and a dialog
   * owned by a row would be torn down mid-edit, losing the reason the operator had just picked.
   *
   * ⚠️ This dialog is NOT a confirm gate. The forward actions on this card state their cost in visible
   * text and write on the tap (see the caps and pack buttons); the dialog exists because the mark takes
   * a REASON, and a reason is the whole basis on which someone later decides whether to undo it — it is
   * rendered on the row and it is what the R1 `ALREADY_PRINTED` event carries. All 22 live marks have
   * one. A bare button would have written a worse record than the queue page's does.
   */
  const [offSystemTarget, setOffSystemTarget] = useState<JobCardGarment | null>(null);
  /** Single-flight for the off-system mark. `isPending` is state and loses a double-tap inside one tick;
   *  this dialog's confirm is exactly the shape where that costs a duplicated write. */
  const offSystemInFlight = useRef(false);

  const orderQuery = useQuery({
    queryKey: ['order', storeId, orderId],
    queryFn: async () => {
      const { data, error } = await client.GET('/orders/{storeId}/{orderId}', {
        params: { path: { storeId, orderId } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the order.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  const orderDetail = orderQuery.data;

  // Finished-garment shelf advisory (plan PR-3): could any of this order's OPEN lines ship whole
  // from the shelf? Advisory only — no reservation; PR-4 adds the action. Keyed under 'garment-stock'
  // so any shelf write live-refreshes it through the change feed.
  const stockRefs = useMemo(
    () =>
      (orderDetail?.lines ?? [])
        .filter((l) => l.workflowState === 'OPEN')
        .map((l) => ({ storeId, orderId, lineId: l.lineId })),
    [orderDetail, storeId, orderId],
  );
  const stockMatchQuery = useQuery({
    queryKey: [
      'garment-stock',
      'match',
      'order',
      storeId,
      orderId,
      // The REF SET, not its length (review F8): two equal-sized OPEN-line sets must not share a
      // cached verdict — this key gates a ledger button and prints a shelf number in its cost copy.
      stockRefs.map((r) => r.lineId).join(','),
    ],
    enabled: stockRefs.length > 0,
    queryFn: async () => {
      const { data, error } = await client.POST('/garment-stock/match', {
        body: { lineRefs: stockRefs },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to check the garment shelf.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data.lines;
    },
  });
  const stockByLine = useMemo(() => {
    const m = new Map<string, StockMatchLine>();
    for (const v of stockMatchQuery.data ?? []) m.set(v.lineId, v);
    return m;
  }, [stockMatchQuery.data]);

  // Distinct sheets this order's lines were claimed into (first-appearance order).
  const batchIds: string[] = [];
  const seenBatch = new Set<string>();
  for (const l of orderDetail?.lines ?? []) {
    if (l.batchId && !seenBatch.has(l.batchId)) {
      seenBatch.add(l.batchId);
      batchIds.push(l.batchId);
    }
  }

  const batchQueries = useQueries({
    queries: batchIds.map((batchId) => ({
      queryKey: ['batch', batchId],
      queryFn: async () => {
        const { data, error } = await client.GET('/batches/{batchId}', {
          params: { path: { batchId } },
        });
        if (error) throw new Error(error?.error?.detail ?? 'Failed to load a sheet.');
        if (!data?.ok) throw new Error('Empty response from the server.');
        return data.data;
      },
    })),
  });

  // A sheet fetch is one of: still loading (pending), settled-with-data (ok), or settled-failed
  // (error). Keep the three apart so the common "just opened, sheets still fetching" case doesn't
  // render as a failure — only a real error gets the red affordance.
  const batchesById = new Map<string, BatchDetail>();
  const batchState = new Map<string, BatchLoadState>();
  batchQueries.forEach((q, i) => {
    const id = batchIds[i];
    if (!id) return;
    if (q.data) {
      batchesById.set(id, q.data);
      batchState.set(id, 'ok');
    } else {
      batchState.set(id, q.isError ? 'error' : 'pending');
    }
  });

  // Rollo Phase 4: outstanding reprints (unpressed reissue batches) for this order. A reissue never
  // repoints line.batchId, so a reprinted garment is invisible in the join above — this dedicated
  // reverse lookup is the only way the order view can warn "don't ship, a reprint is still on the press".
  const reprintsQuery = useQuery({
    queryKey: ['order-reprints', storeId, orderId],
    queryFn: async () => {
      const { data, error } = await client.GET('/orders/{storeId}/{orderId}/reprints', {
        params: { path: { storeId, orderId } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load reprints.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });
  // Two readings of the same query, kept apart on purpose.
  //
  // The VERDICT is `undefined`, not `[]`, until the lookup SUCCEEDS — see `buildJobCard`'s `reprints`
  // param. Keyed on `isSuccess` rather than "data is present" so a failed REFETCH also drops back to
  // "unknown" instead of standing on a stale all-clear. Both non-success states withhold the ship-go.
  const checkedReprints = reprintsQuery.isSuccess ? reprintsQuery.data.reprints : undefined;
  // DISPLAY keeps the last-known refs through a failed refetch, so the operator still sees WHICH sheet is
  // being reprinted instead of just "couldn't check". Safe because it only ever adds a warning: the
  // verdict above is already blocked, and a stale-empty list can't grant an all-clear on its own.
  const reprints: ReprintRef[] = reprintsQuery.data?.reprints ?? [];
  const reprintsByLineId = reprintsByLine(reprints);
  // Split the two arms once, here. They are counted and worded differently everywhere they appear: a
  // BATCHED ref is one SHEET (which may carry several of this order's garments), a QUEUED ref is one
  // ORDER's worth of queued GARMENTS. Adding `.length` across them would mix those units, so the
  // headline counts garments — the thing a human is actually waiting on.
  const batchedReprints = reprints.filter((r) => r.kind === 'batched');
  const queuedReprints = reprints.filter((r) => r.kind === 'queued');
  const queuedGarmentCount = queuedReprints.reduce((n, r) => n + r.entryIds.length, 0);
  const reprintGarmentCount =
    batchedReprints.reduce((n, r) => n + r.lineIds.length, 0) + queuedGarmentCount;

  const card = orderDetail ? buildJobCard(orderDetail, batchesById, checkedReprints) : null;
  const nowMs = Date.now();
  const headline = card ? orderHeadline(card) : null;
  const due = card ? dueStatus(card.dueDate, nowMs) : null;
  /**
   * A due date and a rush flag are PLANNING signals. Once every line has shipped they are history, and
   * `dueStatus` will happily render "45d overdue" in the destructive colour this app reserves for real
   * blockers — on a page the Completed-orders review lane links straight into. Keep both visible (a
   * reviewer wants to know it WAS rush) but strip the alarm.
   *
   * ⚠️ Gated on the ORDER-level state, which is FULFILLED only when every line shipped, so a
   * part-shipped order keeps its alarm. That is the correct direction: work is still owed.
   */
  const shipped = card?.order.fulfillmentState === 'FULFILLED';
  /** Clock time of the last successful load, for the stale-data notice. Null until one lands. */
  const orderLoadedAt = orderQuery.dataUpdatedAt
    ? new Date(orderQuery.dataUpdatedAt).toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      })
    : null;
  /**
   * The order's money state, shown only when it is NOT paid. `PAID_STATUSES` on the server is
   * `{paid, partially_paid}`; everything else is worth an operator's attention. Live today on 3 lines
   * across 2 orders, both `refunded` — and on one of them every line also reads `PAYMENT_PENDING`,
   * which without this chip reads as "chase the customer for money" rather than "they got it back".
   */
  const financial = card ? financialNote(card.order.financialStatus) : null;
  /** "5 pieces · 3 to make" — the board's own helper, so the two surfaces cannot word it differently. */
  const pieceLine = card ? productionPieceLine(card.production) : null;
  /** Garments grouped into lanes — see `groupGarmentsByLane` for why grouped and never re-sorted. */
  const lanes = card ? groupGarmentsByLane(card.garments) : [];
  /**
   * THE ship answer, in one place. ⚠️ `reprintGarmentCount` is passed in rather than derived inside
   * the pure fn because only the page knows the two arms apart: a BATCHED ref is one SHEET that may
   * carry several of this order's garments, a QUEUED ref is a bag of garment ids, and `.length` across
   * them would mix units.
   */
  const verdict = card ? shipVerdict(card, reprintGarmentCount) : null;

  /**
   * The failed write for ONE garment, so the row that failed is the row that says so.
   *
   * ⚠️ Reading `variables.lineId` is only sound because this page runs one write at a time — the
   * `disabled` flags are page-scoped and `pressInFlight` is a hard latch — so `variables` is the
   * mutation that just failed and not an older one. Making the buttons per-row concurrent would
   * break that assumption, which is one reason that change is NOT in this PR.
   */
  const rowErrorFor = (lineId: string): string | undefined => {
    const msg = (e: unknown, fallback: string) =>
      e instanceof Error && e.message ? e.message : fallback;
    if (pressMut.isError && pressMut.variables?.lineId === lineId) {
      return msg(pressMut.error, 'Couldn’t update the press — refresh and retry.');
    }
    if (undoOffSystem.isError && undoOffSystem.variables === lineId) {
      return msg(undoOffSystem.error, 'Couldn’t move it back to the queue — refresh and retry.');
    }
    if (setProduced.isError && setProduced.variables?.lineId === lineId) {
      return msg(
        setProduced.error,
        setProduced.variables?.produced
          ? 'Couldn’t mark it produced — nothing changed. Refresh and retry.'
          : 'Couldn’t move it back to the caps worklist — refresh and retry.',
      );
    }
    if (undoPacked.isError && undoPacked.variables === lineId) {
      return msg(undoPacked.error, 'Couldn’t move it back to the Ship-only worklist — refresh.');
    }
    // ⚠️ The forward pack needs its own arm. Without it a failed money write showed NOTHING on the
    // row — the operator sees an unchanged button and taps again, which is the silent-failure shape
    // this helper exists to prevent. The default names the fail-closed guarantee: the server draws
    // the shelf item BEFORE it flips, and flips only if the draw succeeded, so a failure means
    // nothing moved.
    if (setPacked.isError && setPacked.variables === lineId) {
      return msg(
        setPacked.error,
        'Couldn’t mark it packed — nothing was drawn. Refresh and retry.',
      );
    }
    // Ship-from-stock (PR-4), both directions. The server is fail-closed (draw-before-flip forward;
    // unflip-before-credit undo), so a refusal here means the shelf and the line are unchanged.
    if (shipFromStock.isError && shipFromStock.variables?.lineId === lineId) {
      return msg(
        shipFromStock.error,
        shipFromStock.variables?.pick
          ? 'Couldn’t ship it from the shelf — nothing was drawn. Refresh and retry.'
          : 'Couldn’t return it to production — the pick stands. Refresh and retry.',
      );
    }
    // The forward off-system mark keeps its dialog OPEN on failure and shows the message there, which is
    // where the operator is looking. This arm covers the other exit: they dismiss the dialog, and the row
    // would otherwise look untouched — the silent-failure shape this helper exists to prevent. The
    // default names the fail-closed guarantee: the write is one conditional `updateItem`, so a failure
    // means the line is exactly as it was.
    if (markOffSystem.isError && markOffSystem.variables?.lineId === lineId) {
      return msg(
        markOffSystem.error,
        'Couldn’t mark it printed off-system — nothing was recorded. Refresh and retry.',
      );
    }
    return undefined;
  };
  /**
   * The off-system dialog's error, KEYED TO THE LINE IT IS OPEN ON — the same discriminator
   * {@link rowErrorFor} uses, and for a sharper version of the same reason.
   *
   * ⚠️ `markOffSystem` is page-level and nothing on this page calls `.reset()`, so `isError` stays true
   * indefinitely after a failure. Passing it unkeyed meant a freshly-opened dialog for garment B rendered
   * garment A's failure — "Line is BATCHED; only an OPEN line can be marked already-printed." under B's
   * title, before any POST for B existed. The operator reads B as being on a sheet, abandons the record,
   * and B keeps inflating the `N not recorded` badge this control exists to clear: a wrong REPORT that
   * precedes no real spend, which is exactly the shape that gets acted on because it looks authoritative.
   * Found by an adversarial review, and invisible to the tests because no fixture had two eligible rows.
   */
  const offSystemError = (lineId: string): unknown =>
    markOffSystem.isError && markOffSystem.variables?.lineId === lineId
      ? markOffSystem.error
      : null;
  const isFetching = orderQuery.isFetching || batchQueries.some((q) => q.isFetching);
  const sheetsErrored = [...batchState.values()].some((s) => s === 'error');
  // The "On batch(es) #.." line + press link only make sense once the batch records are in hand.
  const loadedSheets = card ? card.sheets.filter((s) => s.loaded) : [];
  const outstandingScreenPrint = loadedSheets.some(
    (s) => s.screenPrintTotal > s.screenPrintPressed,
  );

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['order', storeId, orderId] });
    qc.invalidateQueries({ queryKey: ['order-reprints', storeId, orderId] });
    qc.invalidateQueries({ queryKey: ['order-events', storeId, orderId] });
    for (const id of batchIds) qc.invalidateQueries({ queryKey: ['batch', id] });
  };

  // ⚠️ MONEY PATH. Per-garment PRESS check-off. DTF placements hit /batches/:id/press; screen-print
  // placements hit the SP worklist mark. Completing the LAST placement of a DTF garment runs
  // `settleLine`, which CONSUMES ITS BLANK and moves the line's workflow state (see onSettled).
  //
  // ⚠️ This comment used to end "Neither endpoint consumes a blank or flips a line — production
  // still completes at mark-printed." That was a Phase-B1 leftover, contradicted by the B2 note in
  // onSettled below and by garment-settle.ts:176, and it sat exactly where someone deciding whether
  // this surface needs the press-station protections (or an owner money review) would read it.
  //
  // Single-flight: one in-flight at a time, every box disabled while pending. The guard is a REF as
  // well as `isPending`, because `isPending` is state — it does not flip until React re-renders, so
  // two taps inside one tick both pass a state-only check.
  const pressMut = useMutation({
    mutationFn: async (v: {
      batchId: string;
      kind: GarmentPlacement['kind'];
      lineId: string;
      location: string;
      pressed: boolean;
    }) => {
      const body = {
        storeId,
        orderId,
        lineId: v.lineId,
        location: v.location,
        pressed: v.pressed,
      };
      const res =
        v.kind === 'dtf'
          ? await client.POST('/batches/{batchId}/press', {
              params: { path: { batchId: v.batchId } },
              body,
            })
          : await client.POST('/screen-print/worklist/mark', {
              body: { batchId: v.batchId, ...body },
            });
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Failed to update the press.');
      if (!res.data?.ok) throw new Error('Empty response from the server.');
    },
    onSettled: (_d, _e, v) => {
      pressInFlight.current = false;
      qc.invalidateQueries({ queryKey: ['batch', v.batchId] });
      // B2: a press can complete/uncomplete the garment (line flips PRINTED), which changes the order's
      // production status — refetch the order so the status badge + "done" banner update.
      qc.invalidateQueries({ queryKey: ['order', storeId, orderId] });
      // Completing the garment runs settleLine, which CONSUMES its blank, and moves the line's
      // workflow state — so the press worklist, the production board and the inventory family are all
      // stale now. Pressing from this card used to leave every one of them wrong.
      qc.invalidateQueries({ queryKey: ['press-worklist'] });
      qc.invalidateQueries({ queryKey: ['production-board'] });
      invalidateInventory(qc);
      // G13. Every mutation on this page records a production event, and this page RENDERS that log —
      // so until now the History section showed a stale record of the action the operator had just
      // taken. ⚠️ A LATENCY fix, not a correctness one: the change feed maps ORDER# writes onto
      // `order-events` under the `orders` domain, so it self-corrected a beat later anyway. What it
      // buys is that the audit trail is right the moment someone looks, which on the one surface whose
      // job is "who did what, when — undos included" is the whole point.
      qc.invalidateQueries({ queryKey: ['order-events', storeId, orderId] });
    },
  });

  // Undo an off-system mark: ALREADY_PRINTED → OPEN, putting the garment back in the queue so it gets a
  // gang sheet. The line only shows here (it left the /orders queue when marked), so this is its only
  // undo path. Single-flight. No blank movement (ALREADY_PRINTED never wrote one — on-hand is
  // reconciled by count), but the line REJOINS the open-work queue, so the queue and the production
  // board move even though inventory does not.
  const undoOffSystem = useMutation({
    mutationFn: async (lineId: string) => {
      const res = await client.POST('/orders/{storeId}/{orderId}/lines/{lineId}/already-printed', {
        params: { path: { storeId, orderId, lineId } },
        body: { alreadyPrinted: false },
      });
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Failed to undo.');
      if (!res.data?.ok) throw new Error('Empty response from the server.');
    },
    onSettled: () => {
      undoInFlight.current = null;
      qc.invalidateQueries({ queryKey: ['order', storeId, orderId] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['production-board'] });
      qc.invalidateQueries({ queryKey: ['order-events', storeId, orderId] });
      // ⚠️ G14, and the ASYMMETRY with the forward mark is deliberate. Attempt 1 of this undo requires
      // UNFULFILLED and RE-QUEUES the line, which puts its blank back into demand — so on-hand
      // projections, coverage and the draft PO's suggested quantity all move and were all left stale.
      // The forward mark (`markOffSystem`) correctly does NOT do this: its gate admits only FULFILLED
      // lines, which were never in demand, so invalidating there would imply a movement that cannot
      // happen. Latent today — all 22 live ALREADY_PRINTED lines are FULFILLED, so attempt 2 runs and
      // nothing re-enters demand — but it is one line and it is a purchasing number.
      invalidateInventory(qc);
    },
  });

  /**
   * Forward off-system mark (PR-6) — the SAME endpoint `undoOffSystem` uses, `alreadyPrinted: true`.
   * The card is the ONLY door for the 259 measured lines that shipped without their production ever
   * being recorded: `queuePartitionFor` returns `undefined` for anything not UNFULFILLED, so `/orders`
   * has no row for them and never will. It is also what makes the undo above reversible.
   *
   * ⚠️ NOT a money path, and that is measured rather than assumed. No blank moves (a blank is drawn at
   * PRESS, by `settleLine`), and because `deriveOrderProduction` short-circuits on FULFILLED *before* its
   * ALREADY_PRINTED branch, the rollup this writes into does not move either — so no ship claim changes.
   * `canMarkPrintedOffSystem` is what holds that true: it admits only already-shipped lines.
   *
   * ⚠️ What it DOES change, and it is the whole value and the whole hazard in one object: the
   * `N not recorded` badge on `/orders/completed` is the only surface in the app that flags a
   * shipped-and-unrecorded line (`isUnrecordedFulfilledLine` in `routes/orders.ts`), and marking a line
   * silences it permanently — on a false claim as easily as a true one. The reason dialog and the
   * garment identity echoed inside it are the mitigation; the R1 event is the trail that survives the
   * undo's `REMOVE alreadyPrintedReason, alreadyPrintedAt, alreadyPrintedBy`.
   *
   * Invalidations mirror `undoOffSystem`, being its inverse, plus `order-events` — this action's whole
   * product is a record, and the card's History is where it is read. ⚠️ That last key is a LATENCY fix,
   * not a correctness one: the change feed already maps ORDER# writes onto `order-events` under the
   * `orders` domain, so History self-corrects a beat later regardless. All six mutations on this page now
   * carry it (audit G13 closed); an earlier version of this comment said the other five still lacked it,
   * which was true for exactly one commit.
   *
   * NOT the inventory family: the line is FULFILLED, so it was never in blank demand and this cannot
   * take it out. ⚠️ If the gate is ever widened to unshipped lines, `invalidateInventory` becomes
   * load-bearing here — the same demand-side move audit G14 records as missing on the undo.
   */
  const markOffSystem = useMutation({
    mutationFn: async (v: { lineId: string; reason: AlreadyPrintedReason }) => {
      const res = await client.POST('/orders/{storeId}/{orderId}/lines/{lineId}/already-printed', {
        params: { path: { storeId, orderId, lineId: v.lineId } },
        body: { alreadyPrinted: true, reason: v.reason },
      });
      if (res.error) {
        throw new Error(res.error?.error?.detail ?? 'Failed to mark it printed off-system.');
      }
      if (!res.data?.ok) throw new Error('Empty response from the server.');
    },
    onSuccess: () => setOffSystemTarget(null),
    onSettled: () => {
      offSystemInFlight.current = false;
      qc.invalidateQueries({ queryKey: ['order', storeId, orderId] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['production-board'] });
      qc.invalidateQueries({ queryKey: ['order-events', storeId, orderId] });
    },
  });

  // Caps mark-produced / undo — the same money endpoint both ways (routes/caps.ts). Marking DRAWS the
  // line's mapped cap blank (fail-closed server-side: no flip without the draw); undoing restores it
  // against the frozen `producedBlankSku`. The FORWARD direction lives on this card — not only /caps —
  // because the caps worklist lists only unshipped caps: a cap fulfilled before anyone recorded the
  // produce has NO /caps row, so this card is its only produce path (the server allows it and draws by
  // currentQty). Same shape for the undo: a produced cap leaves the worklist, so this card is its only
  // reload-surviving reversal path (mirrors undoOffSystem). Single-flight both directions.
  const setProduced = useMutation({
    mutationFn: async (v: { lineId: string; produced: boolean }) => {
      const res = await client.POST('/caps/{storeId}/{orderId}/lines/{lineId}/produced', {
        params: { path: { storeId, orderId, lineId: v.lineId } },
        body: { produced: v.produced },
      });
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Failed to update.');
      if (!res.data?.ok) throw new Error('Empty response from the server.');
    },
    onSettled: () => {
      undoInFlight.current = null;
      qc.invalidateQueries({ queryKey: ['order', storeId, orderId] });
      qc.invalidateQueries({ queryKey: ['caps', 'worklist'] });
      qc.invalidateQueries({ queryKey: ['production-board'] });
      qc.invalidateQueries({ queryKey: ['order-events', storeId, orderId] });
      // Produce draws the cap blank / undo restores it → on-hand and the reorder math both move.
      invalidateInventory(qc);
    },
  });

  // Ship-from-stock (finished-garment-stock v1 PR-4): draw the WHOLE line from the garment shelf and
  // mark it production-complete, or undo that (re-queue + credit the shelf back). Money both ways —
  // same single-flight + invalidation shape as the caps produce above. The server refuses at book
  // zero, on state races, and on a shipped line's undo; every refusal lands as this row's error.
  const shipFromStockInFlight = useRef(false);
  const shipFromStock = useMutation({
    mutationFn: async (v: { lineId: string; pick: boolean }) => {
      const res = await client.POST('/orders/{storeId}/{orderId}/lines/{lineId}/ship-from-stock', {
        params: { path: { storeId, orderId, lineId: v.lineId } },
        body: { pick: v.pick },
      });
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Failed to update.');
      if (!res.data?.ok) throw new Error('Empty response from the server.');
    },
    onSettled: () => {
      shipFromStockInFlight.current = false;
      undoInFlight.current = null;
      qc.invalidateQueries({ queryKey: ['order', storeId, orderId] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['production-board'] });
      qc.invalidateQueries({ queryKey: ['order-events', storeId, orderId] });
      // The pick draws finished garments / the undo credits them back → the shelf tab and the match
      // chips both move (['garment-stock'] is in the inventory family).
      invalidateInventory(qc);
    },
  });

  // Undo a Ship-only mark-packed: PACKED → OPEN, putting the line back on the /fulfill worklist. Same
  // reload-surviving reversal shape as undoProduced. It RESTORES the drawn shelf item (P2b-C) against
  // the frozen `packedItemSlug` — the line above used to say "v1 moves NO stock", which stopped being
  // true when consume-at-pack shipped and contradicted this block's own cache invalidation.
  const undoPacked = useMutation({
    mutationFn: async (lineId: string) => {
      const res = await client.POST('/fulfill/{storeId}/{orderId}/lines/{lineId}/packed', {
        params: { path: { storeId, orderId, lineId } },
        body: { packed: false },
      });
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Failed to undo.');
      if (!res.data?.ok) throw new Error('Empty response from the server.');
    },
    onSettled: () => {
      undoInFlight.current = null;
      qc.invalidateQueries({ queryKey: ['order', storeId, orderId] });
      qc.invalidateQueries({ queryKey: ['fulfill', 'worklist'] });
      qc.invalidateQueries({ queryKey: ['production-board'] });
      qc.invalidateQueries({ queryKey: ['order-events', storeId, orderId] });
      // Undo restores the drawn stock item (P2b-C) — refresh shelf counts like the fulfill page does.
      qc.invalidateQueries({ queryKey: ['stock-items'] });
    },
  });

  // Forward Ship-only pack (PR-5b) — the SAME endpoint the undo uses, packed:true. The card is the only
  // door for a line still unpacked after its order shipped: /fulfill lists UNFULFILLED lines only.
  // Money when the product is mapped to a shelf item (measured: 1 of 21 packs so far), a pure state
  // flip when it is not — `shipOnlyPack` says which, in the row, before the tap.
  const setPacked = useMutation({
    mutationFn: async (lineId: string) => {
      const res = await client.POST('/fulfill/{storeId}/{orderId}/lines/{lineId}/packed', {
        params: { path: { storeId, orderId, lineId } },
        body: { packed: true },
      });
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Failed to mark packed.');
      if (!res.data?.ok) throw new Error('Empty response from the server.');
    },
    onSettled: () => {
      packInFlight.current = false;
      qc.invalidateQueries({ queryKey: ['order', storeId, orderId] });
      qc.invalidateQueries({ queryKey: ['fulfill', 'worklist'] });
      qc.invalidateQueries({ queryKey: ['production-board'] });
      qc.invalidateQueries({ queryKey: ['order-events', storeId, orderId] });
      // A mapped pack draws the shelf item — refresh stock like the fulfill page does.
      qc.invalidateQueries({ queryKey: ['stock-items'] });
    },
  });

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6">
      <div>
        {/* min-h-11 so the phone tap target clears the house 44px floor (/count sets it, /scan 56px);
            it stays an <a>, so right-click and open-in-new-tab still work — which a <button> firing
            history.back() would forfeit. */}
        <Link
          to={backTo.to}
          search={backTo.search}
          className="inline-flex min-h-11 items-center gap-1 text-sm text-muted-foreground hover:text-foreground md:min-h-0"
        >
          <ArrowLeft className="size-4" /> {backTo.label}
        </Link>
      </div>

      {orderQuery.isLoading && (
        <div className="space-y-4">
          <Skeleton className="h-9 w-64" />
          <Skeleton className="h-40 w-full" />
        </div>
      )}

      {orderQuery.isError && !card && (
        <Alert variant="destructive">
          <AlertTitle>Couldn’t load this order</AlertTitle>
          <AlertDescription>
            {orderQuery.error instanceof Error ? orderQuery.error.message : 'Unknown error.'} Check
            the link, or go back to Orders and open it from the list.
          </AlertDescription>
        </Alert>
      )}

      {card && headline && (
        <>
          <header className="flex flex-wrap items-end justify-between gap-3">
            <div className="space-y-1">
              <h1 className="flex flex-wrap items-center gap-2 text-2xl font-semibold tracking-tight">
                {card.order.orderName}
                {/* Rush keeps its red only while the order can still be hurried — see `shipped`. */}
                {card.rush &&
                  (shipped ? (
                    <Badge variant="outline" className={GARMENT_TONE_CLASS.muted}>
                      Was rush
                    </Badge>
                  ) : (
                    <Badge className="border-transparent bg-red-600 text-white hover:bg-red-600">
                      Rush
                    </Badge>
                  ))}
                <Badge variant="outline" className={GARMENT_TONE_CLASS[headline.tone]}>
                  {headline.label}
                </Badge>
                {financial && (
                  <Badge variant="outline" className={GARMENT_TONE_CLASS.attention}>
                    {financial}
                  </Badge>
                )}
              </h1>
              <p className="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
                <span className="rounded bg-muted px-1.5 py-0.5 text-xs">{card.order.storeId}</span>
                <span title={new Date(card.order.shopifyCreatedAt).toLocaleString()}>
                  {relativeAge(card.order.shopifyCreatedAt, nowMs)}
                </span>
                {/* How big this job is, in garments — the one number the card never carried, though the
                    rollup has always sent it. Same helper the production board's cards use, so the two
                    surfaces cannot word the same fact differently. */}
                {pieceLine && <span>· {pieceLine}</span>}
                {/* ⚠️ Dropped entirely once the order has shipped, rather than shown muted.
                    `dueStatus` measures the due date against NOW, so on a shipped order it reports
                    how late the date is TODAY — an order that shipped comfortably on time six weeks
                    ago would read "45d overdue". Muting the colour would keep the false claim and
                    only hide the alarm. Saying nothing is the only thing that is true here without a
                    ship-moment comparison, and no order in the live book carries a due date at all,
                    so that comparison is not worth building yet. */}
                {due && !shipped && (
                  <span className={due.overdue ? 'font-semibold text-destructive' : undefined}>
                    · {due.label}
                  </span>
                )}
                {/* The observed ship moment (R2) — stamped going-forward only, so most of the
                    pre-R2 book has none and shows nothing here. Also gated on the CURRENT state
                    (review): a cancelled fulfillment returns the order to the queues while the
                    once-only stamp remains — the claim hides until Shopify says shipped again. */}
                {card.order.fulfilledAt && card.order.fulfillmentState === 'FULFILLED' && (
                  <span title={new Date(card.order.fulfilledAt).toLocaleString()}>
                    · shipped {formatStamp(card.order.fulfilledAt)}
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* P7-5 — the ONLY route to a shipped order's re-send. A fully-shipped order is invisible
                  to the entire production board (§ 14.1: not even the `shippedUnproduced` lane sees it,
                  because `resetLineOpen` has flipped the line to OPEN), so the office reaches these
                  orders by order number and this button is the door.

                  ⚠️ Gated on ANY LINE being FULFILLED, deliberately NOT on the order-level state:
                  `orderFulfillment` is FULFILLED only when EVERY line shipped
                  (`shopify-ingest.ts:315-320`), so an order shipped in two parcels with one lost — the
                  case § 14.2 names — reads UNFULFILLED and would have no door at all.

                  A permissive entry point with the PREVIEW as the authority: a fully-refunded line also
                  reads FULFILLED, so the dialog may open onto refusals. That is the safe direction —
                  the preview names the exact reason ("0 ordered net of refunds…"), whereas a hidden
                  button on a real lost parcel leaves the operator with nothing. */}
              {isManager &&
                (orderDetail?.lines ?? []).some((l) => l.fulfillmentState === 'FULFILLED') && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-11 md:h-8"
                    onClick={() => setResendOpen(true)}
                  >
                    <PackageX className="size-4" />
                    Re-send…
                  </Button>
                )}
              {/* ⚠️ `h-11 md:h-8` on BOTH header buttons: 44px under a thumb, 32px on a desktop where the
                  pointer is exact. This is the floor the board's own sheet chips document ("`min-h-8`
                  alone was 32px everywhere, under the floor /count (44) and /scan (56) already set") —
                  adopted rather than invented. Measured in Chrome at 390px BEFORE the change: Refresh
                  95x32 and Re-send 111x32, i.e. both under the floor on the phones the shop floor
                  actually holds, not merely on a desktop where the audit first spotted them. */}
              <Button
                variant="outline"
                size="sm"
                className="h-11 md:h-8"
                onClick={refresh}
                disabled={isFetching}
              >
                <RefreshCw className={cn('size-4', isFetching && 'animate-spin')} />
                Refresh
              </Button>
            </div>
          </header>

          <ResendDialog
            storeId={storeId}
            orderId={orderId}
            orderName={card.order.orderName}
            open={resendOpen}
            onOpenChange={setResendOpen}
          />

          {/* ⚠️ `productionComplete`, NOT `production.status === 'PRINTED'` (decision D4 / §8.25): the
              status can't see a reprint, so keying the ship-go on it showed this green all-clear directly
              above the amber "don't ship" alert below. It also stays hidden while the reprint lookup is
              still in flight, so the all-clear can't flash and then retract. */}
          {/* ONE verdict strip, where five stacked alerts used to be.
              ⚠️ This is the alert-shift hazard `docs/production-ui-audit-and-plan-v1.md` left open and
              unowned, and it is the reason the strip is ALWAYS rendered rather than conditional. The
              old stack was four blocks that appeared and disappeared on change-feed refetches — all
              three of this page's query keys are auto-refetched and none is in `MARK_STALE_ONLY` — and
              it sat directly above a checkbox that consumes a blank. A press landing on another
              station could move the tick under the operator's finger.
              That plan forbids the two obvious fixes (MARK_STALE_ONLY on these keys; a fixed
              four-alert min-height). This is the third option it left open and the one the reviewed
              mockup shows: shrink the variable region to a constant by giving the page ONE element in
              one of four states, and demote every PER-GARMENT fact into the row it describes — which
              PR-2 already did (per-row reprint markers with their own sheet links, a Needs-action
              lane, per-row sheet-load failures). */}
          {verdict && (
            <ShipVerdictStrip
              verdict={verdict}
              stale={orderQuery.isError ? (orderLoadedAt ?? 'earlier') : null}
              sheetsErrored={sheetsErrored}
              staleReprints={reprintsQuery.isError ? reprintGarmentCount : 0}
            />
          )}

          {/* ⚠️ NOT a <table>, and now not five columns either.
              The five-track grid could not hold its own content: measured against the live page at a
              1440px viewport, the Stage track resolved to 144px while the "Ship-only — stocked item"
              badge needed 162px, so the badge spilled 18px into the Batch column and collided with
              its em-dash. Widening the track only moves the problem — the real fault is that five
              rigid tracks were carrying three ideas.

              Three zones instead: WHAT it is (identity + qty) · WHERE it is (stage, stamp, sheet) ·
              WHAT TO DO (the control, or a sentence saying why there is nothing). The action zone is
              last on a desktop and full-width on a phone, which is the arrangement PR-7 established
              after the money review — the tick that consumes a blank is never reachable without the
              garment name on screen. `overflow-x-auto` stays as the backstop: a future budget error
              must degrade to a scrollbar, never to an invisible money control. */}
          {card.garments.length === 0 && (
            <Card className="gap-0 overflow-hidden py-0">
              <p className="px-3 py-8 text-center text-sm text-muted-foreground">
                This order has no lines. That usually means every line was removed in Shopify —
                nothing to make, nothing to ship.
              </p>
            </Card>
          )}

          {/* One card per LANE. See `groupGarmentsByLane`: grouped and never re-sorted, because this
              page fires `settleLine` from a checkbox and a row that moves under a finger is how a
              mis-tap spends a blank. */}
          {lanes.map((lane) => (
            <Card
              key={lane.key}
              className={cn('gap-0 overflow-hidden py-0', lane.attention && 'border-amber-300')}
            >
              <div
                className={cn(
                  'flex flex-wrap items-center gap-x-2 gap-y-0.5 border-b px-3 py-2',
                  lane.attention ? 'bg-amber-50 dark:bg-amber-950/40' : 'bg-muted/40',
                )}
              >
                <h2
                  id={`${laneIdBase}-${lane.key}`}
                  className={cn(
                    'text-sm font-semibold',
                    lane.attention && 'text-amber-800 dark:text-amber-300',
                  )}
                >
                  {lane.title}
                </h2>
                <span className="ml-auto text-xs tabular-nums text-muted-foreground">
                  {laneCountLabel(lane)}
                </span>
                <span className="w-full text-xs text-muted-foreground">{lane.blurb}</span>
              </div>
              {/* ⚠️ No column-header strip. The old one read Garment · Qty · Stage · Batch · PRESS,
                  and on a mixed order "Press" was wrong for every non-DTF row — it sat above a cap's
                  "Mark produced" and above a ship-only item's em-dash. With three self-describing
                  zones and a lane title above them there is nothing left for it to name, and it never
                  existed below `lg` anyway. */}
              {/* ⚠️ NAMED, via the lane's own <h2>. An unnamed list announced "list, 7 items" with
                  nothing saying WHICH kind of work — on a four-lane order that is the one thing a
                  screen-reader user needs before they start ticking press check-offs. `aria-labelledby`
                  rather than a duplicated `aria-label` so the name cannot drift from the heading. */}
              <ul aria-labelledby={`${laneIdBase}-${lane.key}`} className="overflow-x-auto text-sm">
                {lane.garments.map((g) => (
                  <GarmentRow
                    key={g.key}
                    g={g}
                    state={g.batchId ? batchState.get(g.batchId) : undefined}
                    reprints={reprintsByLineId.get(g.lineId)}
                    stockMatch={stockByLine.get(g.lineId)}
                    shipFromStockPending={shipFromStock.isPending}
                    onShipFromStock={(lineId, pick) => {
                      // Synchronous latch — the press/pack rule: `isPending` is state and two taps in
                      // one tick both read false. On this control that is a double shelf draw attempt.
                      if (shipFromStockInFlight.current) return;
                      shipFromStockInFlight.current = true;
                      shipFromStock.mutate({ lineId, pick });
                    }}
                    pending={pressMut.isPending}
                    undoPending={undoOffSystem.isPending}
                    onUndoOffSystem={(lineId) => undoOffSystem.mutate(lineId)}
                    producedPending={setProduced.isPending}
                    onSetProduced={(lineId, produced) => setProduced.mutate({ lineId, produced })}
                    undoPackedPending={undoPacked.isPending}
                    onUndoPacked={(lineId) => undoPacked.mutate(lineId)}
                    rowError={rowErrorFor(g.lineId)}
                    onShippedUndo={(garment, undo) => setConfirmUndo({ g: garment, undo })}
                    offSystemPending={markOffSystem.isPending}
                    onMarkOffSystem={(garment) => setOffSystemTarget(garment)}
                    packPending={setPacked.isPending}
                    onSetPacked={(lineId) => {
                      // Single-flight, same argument as the press latch: `isPending` is state and two
                      // taps in one tick both read false. This one can draw a shelf item.
                      if (packInFlight.current) return;
                      packInFlight.current = true;
                      setPacked.mutate(lineId);
                    }}
                    onPress={(p, pressed) => {
                      if (!g.batchId) return;
                      // Synchronous latch — see `pressInFlight`. The disabled attribute alone loses a
                      // same-tick double tap, and on this control that is a blank.
                      if (pressInFlight.current) return;
                      pressInFlight.current = true;
                      pressMut.mutate({
                        batchId: g.batchId,
                        kind: p.kind,
                        lineId: g.lineId,
                        location: p.location,
                        pressed,
                      });
                    }}
                  />
                ))}
              </ul>
            </Card>
          ))}

          {loadedSheets.length > 0 && (
            <section className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
              <span>On {loadedSheets.length === 1 ? 'batch' : 'batches'}:</span>
              {loadedSheets.map((s) => (
                <Link
                  key={s.batchId}
                  to="/batches"
                  search={{ batch: s.batchId }}
                  className="underline underline-offset-2 hover:no-underline"
                >
                  {s.seq ? `#${s.seq}` : '(unnumbered)'}
                  {s.reissueOf ? ' · reprint' : ''}
                </Link>
              ))}
              {outstandingScreenPrint && (
                <Link to="/press" className="font-medium text-foreground underline">
                  Open press station →
                </Link>
              )}
            </section>
          )}

          {/* Production-records R2: the append-only who/when/what — collapsed, fetched on expand. */}
          <OrderHistorySection storeId={storeId} orderId={orderId} garments={card.garments} />
        </>
      )}

      {confirmUndo && (
        <ShippedUndoDialog
          garment={confirmUndo.g}
          undo={confirmUndo.undo}
          onClose={() => setConfirmUndo(null)}
          onConfirm={() => {
            // Synchronous latch, not the mutations' `isPending`: that is state and does not flip until
            // React re-renders, so two taps in one tick both read false. On two of these three the
            // second tap is a second ledger write. Keyed per undo so a different row or a different
            // lane is never silently swallowed. Released in each mutation's onSettled.
            const key = `${confirmUndo.undo.kind}:${confirmUndo.g.lineId}`;
            if (undoInFlight.current === key) return;
            undoInFlight.current = key;
            const { g, undo } = confirmUndo;
            setConfirmUndo(null);
            switch (undo.kind) {
              case 'off-system':
                undoOffSystem.mutate(g.lineId);
                break;
              case 'produce':
                setProduced.mutate({ lineId: g.lineId, produced: false });
                break;
              case 'pack':
                undoPacked.mutate(g.lineId);
                break;
              case 'stock-pick':
                // The CREDITING route, never the generic undo — the server gates the latter (F2/F3).
                shipFromStock.mutate({ lineId: g.lineId, pick: false });
                break;
            }
          }}
        />
      )}

      {offSystemTarget && (
        <MarkOffSystemDialog
          garment={offSystemTarget}
          pending={markOffSystem.isPending}
          error={offSystemError(offSystemTarget.lineId)}
          onClose={() => setOffSystemTarget(null)}
          onConfirm={(reason) => {
            // Synchronous latch — `isPending` is state and two taps inside one tick both read false.
            //
            // ⚠️ CORRECTED after a review: an earlier version of this comment said a duplicated POST
            // "would overwrite the reason and the who/when of a record that already existed". It cannot.
            // The route reads the line first and skips the write entirely once `workflowState` is already
            // ALREADY_PRINTED, so a replay is a no-op that answers 200. The real cost of a lost double-tap
            // is the opposite: two POSTs that both read OPEN race the `workflowState = :open` condition,
            // one wins, and the LOSER surfaces a red "Line changed state; reload and try again." on a mark
            // that in fact succeeded — a false failure report on a completed action, which is what makes
            // an operator undo and redo it. The latch stays; only its stated reason was wrong.
            // Released in the mutation's onSettled.
            if (offSystemInFlight.current) return;
            offSystemInFlight.current = true;
            markOffSystem.mutate({ lineId: offSystemTarget.lineId, reason });
          }}
        />
      )}
    </div>
  );
}

/**
 * Capture the REASON for a forward off-system mark, then write it (PR-6).
 *
 * ⚠️ This is not a confirm gate. The card's other two forward actions write on the tap and state their
 * cost in visible text, and adding friction to a spend is explicitly the owner decision those buttons
 * refuse to take on themselves. Here nothing is spent — the dialog exists because
 * `alreadyPrintedReason` has to come from somewhere, and it is the field the row renders back and the R1
 * `ALREADY_PRINTED` event carries. Marking without one would write a poorer record than the queue page's
 * own mark-printed does.
 *
 * ⚠️ The reason VOCABULARY is shared with `/orders` (`ALREADY_PRINTED_REASONS` in `orders-queue.ts`,
 * moved there in PR-1 for exactly this) because those four strings label a persisted enum and must not
 * drift. The surrounding prose is deliberately NOT shared: the queue's dialog speaks to unshipped work
 * ("will not be put on a gang sheet"), and every line reaching this one has already shipped, so the true
 * sentences are different ones. Two populations, two descriptions, one vocabulary.
 *
 * Stays OPEN on failure with the message inside it, matching the queue page's bulk dialog: the reason the
 * operator picked survives the retry.
 */
function MarkOffSystemDialog({
  garment,
  pending,
  error,
  onClose,
  onConfirm,
}: {
  garment: JobCardGarment;
  pending: boolean;
  error: unknown;
  onClose: () => void;
  onConfirm: (reason: AlreadyPrintedReason) => void;
}) {
  const [reason, setReason] = useState<AlreadyPrintedReason>('MANUAL_SHEET');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Mark printed off-system</DialogTitle>
          <DialogDescription>
            {garment.title}
            {garment.variantTitle ? ` · ${garment.variantTitle}` : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          {/* Says what this IS, because it is the unusual one: a record being filled in after the fact,
              not an instruction about what happens next. The queue page's dialog warns "only do this if
              the transfer is already done" — here the garment has demonstrably shipped, so the warning
              that matters is the opposite one: this changes no state anything acts on. */}
          <p>This garment shipped without its production ever being recorded.</p>
          <p>{OFF_SYSTEM_MARK_COST}</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="off-system-reason">How was it printed?</Label>
          <Select
            value={reason}
            onValueChange={(v) => setReason(v as AlreadyPrintedReason)}
            disabled={pending}
          >
            <SelectTrigger id="off-system-reason" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ALREADY_PRINTED_REASONS.map((r) => (
                <SelectItem key={r.value} value={r.value}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {error != null && (
          <Alert variant="destructive">
            <AlertTitle>Couldn’t record it</AlertTitle>
            <AlertDescription>
              {error instanceof Error && error.message
                ? error.message
                : 'Nothing was recorded. Try again.'}
            </AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={() => onConfirm(reason)} disabled={pending}>
            {pending ? <Loader2 className="size-4 animate-spin" /> : <Printer className="size-4" />}
            Record it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Confirm an undo that the shop cannot walk back — see `shippedUndo` for why these three are one-way
 * once a line stops being UNFULFILLED.
 *
 * The dialog restates the same three sentences the row does, deliberately: an operator who tapped
 * without reading the row gets one more chance to read them, and a reviewer diffing the two can see
 * they come from the same source rather than being written twice.
 */
function ShippedUndoDialog({
  garment,
  undo,
  onClose,
  onConfirm,
}: {
  garment: JobCardGarment;
  undo: ShippedUndo;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{UNDO_CONFIRM_TITLE[undo.kind]}</DialogTitle>
          <DialogDescription>
            {garment.title}
            {garment.variantTitle ? ` · ${garment.variantTitle}` : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          <p>{undo.lead}</p>
          {/* The ledger sentence carries the emphasis only when a ledger actually moves. */}
          <p className={undo.movesStock ? 'font-medium text-amber-700 dark:text-amber-400' : ''}>
            {undo.cost}
          </p>
          <p className="text-muted-foreground">{undo.strand}</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Leave it as it is
          </Button>
          <Button variant="destructive" onClick={onConfirm}>
            {UNDO_CONFIRM_ACTION[undo.kind]}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Titles and confirm labels per undo. Separate from the sentences so the copy for the three controls
 *  can't drift into one generic "Are you sure?" — the label states the action, not the question. */
const UNDO_CONFIRM_TITLE: Record<ShippedUndo['kind'], string> = {
  'off-system': 'Clear the off-system print record?',
  produce: 'Undo this cap’s produce?',
  pack: 'Undo this pack?',
  'stock-pick': 'Return this line to production?',
};
const UNDO_CONFIRM_ACTION: Record<ShippedUndo['kind'], string> = {
  'off-system': 'Clear the record',
  produce: 'Undo the produce',
  pack: 'Undo the pack',
  'stock-pick': 'Return to production',
};

/** Whether a garment's sheet header has loaded, is still fetching, or failed. */
type StockMatchLine = components['schemas']['GarmentStockMatchResponse']['lines'][number];
type BatchLoadState = 'pending' | 'error' | 'ok';

/** Tone per verdict state. `nothing` is muted on purpose — it is neither a go nor a warning. */
const VERDICT_CLASS: Record<ShipVerdict['state'], string> = {
  go: 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-100',
  stop: 'border-red-300 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100',
  unknown:
    'border-blue-300 bg-blue-50 text-blue-900 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-100',
  nothing: 'border-border bg-muted text-muted-foreground',
  shipped: 'border-border bg-muted text-muted-foreground',
};

/**
 * The one always-present band above the garment lanes. See its call site for why it is unconditional.
 *
 * `role="status"` rather than `role="alert"`: it is present at all times and changes state, so an
 * assertive live region would interrupt a screen-reader user on every refetch. The per-row money
 * failures are the assertive ones.
 */
function ShipVerdictStrip({
  verdict,
  stale,
  sheetsErrored,
  staleReprints,
}: {
  verdict: ShipVerdict;
  /** Clock time of the last good load, when a refresh has failed since. */
  stale: string | null;
  sheetsErrored: boolean;
  /** Reprints the LAST good lookup found, when the current one failed. */
  staleReprints: number;
}) {
  /**
   * ⚠️ Qualifiers EXTEND THE REASON rather than adding their own paragraphs. As separate blocks they
   * mounted and unmounted on a refetch and sat outside the height floor below, so a transient 500 on
   * a sheet fetch moved every garment row — the exact shift this strip exists to prevent, reintroduced
   * by the elements meant to inform about it. Folded into one sentence they stay inside the two-line
   * budget the floor already covers.
   */
  const qualifiers = [
    sheetsErrored ? 'A sheet couldn’t be loaded — the rows show the state we have.' : null,
    // ⚠️ Without this, a FAILED refetch of the reprint lookup silently demotes a known "do not ship"
    // to the blue "can't confirm": `reprintOutstanding` is 0 while unchecked, so the reprint arm
    // cannot fire, even though the last good answer found reprints and the rows still show them.
    staleReprints > 0
      ? `The last check found ${staleReprints} reprint${staleReprints === 1 ? '' : 's'} outstanding.`
      : null,
    stale ? `Couldn’t refresh — showing the order as of ${stale}.` : null,
  ].filter(Boolean) as string[];
  const Icon =
    verdict.state === 'stop'
      ? AlertTriangle
      : verdict.state === 'unknown'
        ? RefreshCw // its reason literally says "try Refresh"
        : PackageCheck;
  return (
    <section
      role="status"
      /* ⚠️ `min-h` below `sm`, measured not guessed. The whole point of this strip is that nothing
         above the garment rows may move them — a press landing on another station can flip the
         verdict on a refetch, and this sits directly above a checkbox that consumes a blank. On a
         desktop every state renders at exactly 44px, so nothing is needed. On a 390px phone the
         reason wraps for three of the five states and the strip measured 68px vs 88px. Shortening the
         tallest state's copy took the spread from 36px to 20px; this takes it to 0. 20px was still
         close to the 24px height of the checkbox below it — near enough to move one under a finger.
         ⚠️ This is NOT the "fixed four-alert min-h" `docs/production-ui-audit-and-plan-v1.md` forbids.
         That reserved space for alerts that are usually absent; this pins ONE always-present element
         to the height it already reaches, and costs 20px in two of five states. */
      className={cn(
        'min-h-[5.5rem] rounded-lg border px-3 py-2.5 text-sm sm:min-h-0',
        VERDICT_CLASS[verdict.state],
      )}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Icon className="size-4 shrink-0" />
        <span className="font-semibold">{verdict.headline}</span>
        <span className="text-xs opacity-90">{[verdict.reason, ...qualifiers].join(' ')}</span>
        {verdict.lanes.length > 0 && (
          <span className="ml-auto flex flex-wrap gap-1">
            {verdict.lanes.map((l) => (
              <Badge
                key={l.key}
                variant="outline"
                className="border-current/25 bg-background/60 font-normal tabular-nums"
              >
                {l.label} {l.done}/{l.total}
              </Badge>
            ))}
          </span>
        )}
      </div>
    </section>
  );
}

/**
 * The order's money state as a badge, or null when it is paid and there is nothing to say.
 *
 * ⚠️ The paid set mirrors the server's `PAID_STATUSES` (`{paid, partially_paid}`), which is what
 * drives a line to `mappingStatus: 'PAYMENT_PENDING'`. Keeping them in step is the whole point: a
 * REFUNDED order's lines carry PAYMENT_PENDING, and with no badge the card told an operator to chase
 * a customer who had already been given their money back.
 *
 * Unknown statuses fall through to the raw token rather than being swallowed — Shopify owns this
 * vocabulary and a value we have not seen is still worth showing.
 */
function financialNote(status: string | null): string | null {
  if (!status || status === 'paid' || status === 'partially_paid') return null;
  switch (status) {
    case 'refunded':
      return 'Refunded';
    case 'partially_refunded':
      return 'Partly refunded';
    case 'pending':
      return 'Payment pending';
    case 'voided':
      return 'Voided';
    default:
      return status;
  }
}

/**
 * Three zones from `lg` up: WHAT it is · WHERE it is · WHAT TO DO. Below that the row is a card and
 * they stack — same DOM, two layouts (receiving-page.tsx / inventory-page.tsx).
 *
 * ⚠️ `lg` (1024px), NOT `md`, and this is measured rather than chosen. **The sidebar goes inline at
 * the same 768px `md:` fires at**, so a 768px iPad leaves a content box of just 464px — I tried `md`
 * and measured the result: `102px | 72px | 240px`, i.e. the garment name squeezed to 102px and the
 * middle zone spilling 58px. The old five-track grid waited for `lg` for exactly this reason; that
 * was not conservatism.
 *
 * ⚠️ The third track is `minmax(9rem, 0.9fr)`, not `minmax(9rem, 15rem)`. A bounded max makes the
 * track RIGID, and CSS grid maximises rigid tracks before it expands flexible ones — at 464px the
 * action zone took its full 240px and starved the other two. A flex-factor max keeps a floor for the
 * money control while letting it share free space proportionally instead of grabbing it.
 */
const GARMENT_GRID =
  'lg:grid lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(9rem,0.9fr)] lg:items-start lg:gap-x-3';

/**
 * The garment's photo, with its size stamped on the corner (PR-4).
 *
 * ⚠️ **`object-contain`, not `object-cover`** — and this is not a style preference. These are Shopify
 * product mockups on white, not photographs: `cover` crops a decal or a keychain down to an
 * unrecognisable patch of its own middle. The catalog page's `ProductThumb` uses `cover` at a
 * different size with an icon fallback, which is right for a grid of products and wrong for a row an
 * operator identifies a garment from — so this is its own component rather than a fourth prop on that
 * one.
 *
 * ⚠️ **The chip is the reason the photo is safe to show.** `featuredImage` is product-level, so every
 * variant of a shirt shares one picture; the size stamped on it is what makes a picture that belongs
 * to another variant disagree with itself, next to a control that consumes a blank. See
 * `garmentSizeChip`. If the chip is ever dropped, drop the image with it.
 *
 * ⚠️ `alt=""` and NOT a link, both deliberate. The row's own text carries the garment's identity, so
 * the image is decorative; giving it an accessible name (or an anchor) would put a product title into
 * the page's link/name space, where three suites already query links by loose regex.
 */
function GarmentThumb({ g }: { g: JobCardGarment }) {
  const [failed, setFailed] = useState(false);
  const chip = garmentSizeChip(g);
  const showImage = Boolean(g.imageUrl) && !failed;
  return (
    // `self-start` so the wrapper cannot stretch to the row's height. The parent already sets
    // `items-start`, but the approved mockup shipped this exact bug — its `.thumbwrap` stretched and
    // hung the chip 15px BELOW the picture, in the gutter — so the corner the chip is anchored to is
    // pinned here rather than left to the parent.
    <div className="relative shrink-0 self-start">
      <div className="grid size-[54px] place-items-center overflow-hidden rounded-md border bg-white lg:size-[60px]">
        {showImage ? (
          <img
            // 2× the 60px box so the Shopify CDN downscales a ~2000px original to a few KB.
            src={withImageWidth(g.imageUrl as string, 120)}
            alt=""
            width={60}
            height={60}
            loading="lazy"
            decoding="async"
            onError={() => setFailed(true)}
            className="block size-full object-contain"
          />
        ) : (
          // A broken URL and an absent one land here together, on purpose: both mean "no picture",
          // and neither says anything about the garment.
          <span className="p-0.5 text-center text-[9px] font-bold uppercase leading-tight tracking-[0.04em] text-zinc-400">
            No image
          </span>
        )}
      </div>
      {chip && (
        <span
          className={cn(
            'absolute -bottom-[3px] -right-[3px] rounded-[5px] border-[1.5px] border-white px-1 py-px text-[9.5px] font-extrabold leading-[1.45] tracking-[0.02em] text-white',
            // Violet for youth. The token is already Y-prefixed, so the colour is redundancy rather
            // than the only signal — which is the point on a mislabel guard.
            chip.youth ? 'bg-violet-700' : 'bg-zinc-900',
          )}
        >
          {chip.text}
        </span>
      )}
    </div>
  );
}

/**
 * One of the three "undo" controls, plus — on a line that can no longer come back — a visible
 * statement of what tapping it does, and a confirm instead of an immediate write.
 *
 * ⚠️ All three of these are irreversible once a line stops being UNFULFILLED, and two of them move a
 * ledger on the way out. See `shippedUndo` for the two-attempt conditional write that makes that true;
 * the short version is that the undo's fulfilment guard has a fallback which drops the line off every
 * worklist, and no re-ingest ever puts it back.
 *
 * The cost was previously stated only in a `title` tooltip — invisible on the phones the shop floor
 * actually uses, which is the same argument this file already makes for the "from shelf" tag and the
 * reprint warning. On the ordinary UNFULFILLED path nothing changes: no sentence, no confirm, same
 * plain button. Friction there would teach people to tap through the one case that matters.
 */
function UndoControl({
  label,
  title,
  pending,
  shipped,
  onPlain,
  onShipped,
}: {
  label: string;
  /** Tooltip for the reversible case only — a shipped line gets the visible sentence instead. */
  title: string;
  pending: boolean;
  /** Non-undefined ⇒ this line cannot come back; explain and confirm. */
  shipped?: ShippedUndo;
  onPlain: () => void;
  onShipped: (undo: ShippedUndo) => void;
}) {
  const button = (
    <Button
      variant="ghost"
      size="sm"
      className="h-11 whitespace-nowrap px-3 text-xs lg:h-8 lg:px-2"
      disabled={pending}
      onClick={() => (shipped ? onShipped(shipped) : onPlain())}
      title={shipped ? undefined : title}
    >
      {pending ? <Loader2 className="size-3 animate-spin" /> : <RotateCcw className="size-3" />}
      {label}
    </Button>
  );
  if (!shipped) return button;
  return (
    <div>
      {button}
      <p
        className={cn(
          // Width-capped only where the action zone is a narrow grid track. Below `lg` the zone is the
          // full row, and holding this to 15rem there wrapped a six-line paragraph into a column two
          // thirds the available width — measured at 390px, where the shop actually reads it.
          'mt-1 text-[11px] leading-snug lg:max-w-[15rem]',
          // Amber only when a ledger actually moves. The off-system undo costs no stock, and tinting it
          // the same would make the two that DO spend look ordinary.
          shipped.movesStock ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground',
        )}
      >
        {shipped.lead} {shipped.cost} {shipped.strand}
      </p>
    </div>
  );
}

function GarmentRow({
  g,
  state,
  reprints,
  stockMatch,
  shipFromStockPending,
  onShipFromStock,
  pending,
  undoPending,
  onUndoOffSystem,
  producedPending,
  onSetProduced,
  undoPackedPending,
  onUndoPacked,
  rowError,
  onPress,
  onShippedUndo,
  packPending,
  onSetPacked,
  offSystemPending,
  onMarkOffSystem,
}: {
  g: JobCardGarment;
  state?: BatchLoadState;
  reprints?: ReprintRef[];
  /** The finished-garment shelf's advisory verdict for this line (plan PR-3), when checked. */
  stockMatch?: StockMatchLine;
  shipFromStockPending: boolean;
  /** Ship-from-stock (PR-4): pick=true draws the shelf; pick=false is the crediting undo. */
  onShipFromStock: (lineId: string, pick: boolean) => void;
  pending: boolean;
  undoPending: boolean;
  onUndoOffSystem: (lineId: string) => void;
  producedPending: boolean;
  onSetProduced: (lineId: string, produced: boolean) => void;
  undoPackedPending: boolean;
  onUndoPacked: (lineId: string) => void;
  /** This garment's own failed write, so the message sits with the control that failed. */
  rowError?: string;
  onPress: (p: GarmentPlacement, pressed: boolean) => void;
  /** Ask before an undo that cannot be undone. The row never writes on this path itself. */
  onShippedUndo: (g: JobCardGarment, undo: ShippedUndo) => void;
  packPending: boolean;
  onSetPacked: (lineId: string) => void;
  offSystemPending: boolean;
  /** Opens the reason dialog. The row never writes on this path — the mark needs a reason first. */
  onMarkOffSystem: (g: JobCardGarment) => void;
}) {
  const stage = garmentStage(g);
  // Derived from the garment, so the sentence and the confirm always describe the button on screen.
  const shipped = shippedUndo(g);
  const pack = shipOnlyPack(g);
  const canMarkOffSystem = canMarkPrintedOffSystem(g);
  const idle = garmentIdleNote(g, state === 'error');
  const idleNote = idle ? (
    <p className="text-xs leading-snug text-muted-foreground">
      {idle.text}
      {idle.to && (
        <>
          {' '}
          <Link to={idle.to} className="underline underline-offset-2 hover:no-underline">
            {idle.linkLabel} →
          </Link>
        </>
      )}
    </p>
  ) : null;
  return (
    <li className={cn('border-b px-3 py-3 last:border-b-0 lg:py-2.5', GARMENT_GRID)}>
      {/* ZONE 1 — WHAT IT IS. Identity and quantity travel together at every width now; they used to
          be separate grid tracks, so on a desktop the count sat in its own 40px column two cells away
          from the name it counted.
          ⚠️ The title WRAPS rather than truncating — a truncated garment name is the one thing an
          operator must not be guessing at while ticking a press check-off. */}
      {/* Three children now, so the qty is pushed right by the middle one GROWING rather than by
          `justify-between` — which with a third child would have opened a gap between the picture
          and the name it belongs to. */}
      <div className="flex items-start gap-2.5">
        <GarmentThumb g={g} />
        <div className="min-w-0 flex-1">
          <div className="font-medium">{g.title}</div>
          {g.variantTitle && <div className="text-xs text-muted-foreground">{g.variantTitle}</div>}
          {g.sku && <div className="font-mono text-xs text-muted-foreground">{g.sku}</div>}
        </div>
        {/* The count, the way the reviewed mockup shows it: the number is what an operator scans, so
            it is big and the unit is a caption under it. The caption is dropped on a cancelled row,
            where the value is an em-dash and "PCS" underneath it would be nonsense. */}
        <span className="shrink-0 text-right leading-none">
          <span className="text-base font-bold tabular-nums">{garmentQtyLabel(g)}</span>
          {g.fulfillmentState !== 'CANCELLED' && (
            <span className="mt-0.5 block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {g.quantity === 1 ? 'pc' : 'pcs'}
            </span>
          )}
        </span>
      </div>
      {/* ZONE 2 — WHERE IT IS. Stage, who/when, and the sheet it lives on, which used to be a
          separate track and is the same idea. */}
      <div className="mt-2 lg:mt-0">
        <Badge variant="outline" className={cn('font-normal', GARMENT_TONE_CLASS[stage.tone])}>
          {stage.label}
        </Badge>
        {/* Production-records R0: the who/when the API always carried and this card used to hide.
            Checks class+state (not the stage label) so a FULFILLED line still shows how its
            production was recorded — the review case. */}
        {garmentStamp(g) && <p className="mt-1 text-xs text-muted-foreground">{garmentStamp(g)}</p>}
        {/* WHY it was taken off the sheet. `alreadyPrintedReason` has been on the view and mapped
            onto the garment all along, and rendered nowhere — so the only place to read it was the
            collapsed history, as a raw enum token. It is the whole basis for deciding whether to
            undo the mark, and the undo button is right there in this row. */}
        {/* Finished-garment shelf (PR-3/PR-4). A COVERABLE line carries the action in ZONE 3 with
            its own cost copy, so zone 2 only speaks when the shelf has SOME but not enough — the
            case with no button and real information in it. */}
        {g.workflowState === 'OPEN' &&
          stockMatch &&
          !stockMatch.coverable &&
          (stockMatch.onHand ?? 0) > 0 && (
            <p className="mt-1 text-xs text-muted-foreground">
              {stockMatch.onHand} of {stockMatch.coverQty} on the garment shelf — not enough to ship
              from stock
            </p>
          )}
        {/* The pick's stamp: WHY this line reads already-printed. `alreadyPrintedReason` is unset on
            a shelf pick (no enum change), so this line is the reason's stand-in. */}
        {g.workflowState === 'ALREADY_PRINTED' && g.stockPickId && (
          <p className="mt-1 text-xs text-muted-foreground">
            shipped from the garment shelf · {g.stockPickQty ?? '?'} taken
          </p>
        )}
        {g.workflowState === 'ALREADY_PRINTED' &&
          alreadyPrintedReasonLabel(g.alreadyPrintedReason) && (
            <p className="mt-1 text-xs text-muted-foreground">
              {alreadyPrintedReasonLabel(g.alreadyPrintedReason)}
            </p>
          )}
        {g.productionClass === 'FULFILLMENT_ONLY' &&
          g.fulfillWorkflowState === 'PACKED' &&
          g.packedItemSlug && (
            <p className="text-xs text-muted-foreground">from shelf: {g.packedItemSlug}</p>
          )}
        <div className="mt-1.5 text-muted-foreground">
          {!g.batchId ? null : state === 'error' ? (
            <span className="text-destructive">Batch — couldn’t load</span>
          ) : state === 'pending' ? (
            <span className="text-xs">loading…</span>
          ) : (
            <span>
              <Link
                to="/batches"
                search={{ batch: g.batchId }}
                className="underline underline-offset-2 hover:no-underline"
                title="Open this sheet on the Gang sheets page"
              >
                {g.batchSeq ? `Batch #${g.batchSeq}` : 'Batch'}
              </Link>
              {g.batchStatus && (
                <span className="ml-1 text-xs">· {g.batchStatus.toLowerCase()}</span>
              )}
              {g.batchPrintedAt && (
                <span className="ml-1 text-xs">· printed {formatStamp(g.batchPrintedAt)}</span>
              )}
            </span>
          )}
          {/* Two arms, rendered on their own terms. A QUEUED garment has no sheet yet, so there is no
            number to show and no "on the press" claim to make — saying so honestly is the difference
            between "go find sheet #12" and "it hasn't been printed yet". */}
          {reprints?.map((r) =>
            r.kind === 'batched' ? (
              <div
                key={r.batchId}
                className="mt-1 text-xs font-medium text-amber-700 dark:text-amber-400"
              >
                {/* The sheet number is a link here too — this marker sits on the garment itself, which
                  is where an operator decides what to go and do. */}
                ⚠ Reprint{' '}
                <Link
                  to="/batches"
                  search={{ batch: r.batchId }}
                  className="underline underline-offset-2 hover:no-underline"
                >
                  {r.seq ? `#${r.seq}` : 'sheet'}
                </Link>{' '}
                · {r.status.toLowerCase()}
                {/* ⚠️ Visible, not a `title`. This is the instruction that stops the garment shipping,
                  and the shop floor is on phones, which cannot hover — the same argument this file
                  already makes for the "from shelf" tag two blocks down. */}
                <span className="block font-normal">Don’t ship until it’s marked printed.</span>
              </div>
            ) : (
              <div
                key={r.entryIds.join(',')}
                className="mt-1 text-xs font-medium text-amber-700 dark:text-amber-400"
              >
                ⚠ In reprint queue · {r.entryIds.length} garment
                {r.entryIds.length === 1 ? '' : 's'}
                <span className="block font-normal">
                  Not printed yet — it goes on the next reprint sheet. Don’t ship.
                </span>
              </div>
            ),
          )}
        </div>
      </div>
      {/* ZONE 3 — WHAT TO DO. Full-width on its own line on a phone; it used to be column 5 of 5,
          reachable only by scrolling the identity off screen. ⚠️ It is NEVER empty: where there is no
          control, `garmentIdleNote` says why in a sentence, because an unexplained em-dash is
          indistinguishable from a bug — and on the unpacked ship-only row it was one. */}
      <div className="mt-4 lg:mt-0">
        {g.workflowState === 'ALREADY_PRINTED' && g.stockPickId ? (
          // Ship-from-stock undo — NOT the off-system undo: this one credits the garment shelf back
          // against the frozen pick (the generic door refuses these lines server-side for the same
          // reason). A FULFILLED line's undo is refused by the server — the shirts shipped; the
          // pick stands — so `shipped` routes it through the confirm that says so.
          <UndoControl
            label="Return to production"
            title="Shipped from the garment shelf. Put it back in the queue and credit the shelf."
            pending={shipFromStockPending}
            shipped={shipped}
            onPlain={() => onShipFromStock(g.lineId, false)}
            onShipped={(u) => onShippedUndo(g, u)}
          />
        ) : g.workflowState === 'ALREADY_PRINTED' ? (
          <UndoControl
            label="Still needs printing"
            title="Marked printed off-system. Put it back in the queue so it gets a gang sheet."
            pending={undoPending}
            shipped={shipped}
            onPlain={() => onUndoOffSystem(g.lineId)}
            onShipped={(u) => onShippedUndo(g, u)}
          />
        ) : g.productionClass === 'NON_DTF' && g.nonDtfWorkflowState === 'PRODUCED' ? (
          <UndoControl
            label="Still to produce"
            title="Marked produced. Put it back on the caps worklist."
            pending={producedPending}
            shipped={shipped}
            onPlain={() => onSetProduced(g.lineId, false)}
            onShipped={(u) => onShippedUndo(g, u)}
          />
        ) : g.productionClass === 'NON_DTF' &&
          g.workflowState === 'OPEN' &&
          g.fulfillmentState !== 'CANCELLED' ? (
          // Forward produce for a cap. On this card — not only /caps — because the caps worklist shows
          // only unshipped caps: a cap fulfilled before anyone recorded the produce has no /caps row,
          // and this button is its only produce path. Money: draws the mapped cap blank from stock
          // (an unmapped cap just flips). Cancelled caps are excluded — they owe no production and no
          // blank, so offering the action is pure noise. ⚠️ `workflowState === 'OPEN'` is load-bearing:
          // it is this caller's copy of the caps-worklist pristine-DTF gate. A line reclassified to
          // NON_DTF mid-flight keeps its DTF history (BATCHED/PRINTED/…); offering produce there draws
          // its APPAREL blank, then the server flip 409s on the same guard — an orphaned consume with
          // no undo path, double-consumed again later at settle.
          <div>
            <Button
              variant="outline"
              size="sm"
              className="h-11 whitespace-nowrap px-3 text-xs lg:h-8 lg:px-2"
              disabled={producedPending}
              onClick={() => onSetProduced(g.lineId, true)}
            >
              {producedPending ? (
                <Loader2 className="size-3 animate-spin" />
              ) : (
                <Check className="size-3" />
              )}
              Mark produced
            </Button>
            {/* ⚠️ What this button SPENDS, in visible text. It was stated only in a `title` tooltip,
                which no touch device can show — so on a phone the operator tapped a money button with
                no statement of its cost at all. Deliberately NOT a confirm dialog: that would be a new
                money GATE and needs its own owner decision, not a drive-by addition. */}
            <p className="mt-1 max-w-[15rem] text-[11px] leading-snug text-muted-foreground">
              Draws the mapped cap blank for the full ordered quantity (refunds excluded), even when
              Qty shows 0 because the order already shipped.
            </p>
          </div>
        ) : pack ? (
          // Forward pack for a Ship-only line (PR-5b). On this card — not only /fulfill — because the
          // Ship-only worklist lists UNFULFILLED lines only: a line still unpacked after its order
          // shipped has NO worklist row and never will, which is 8 measured lines with no other door.
          // ⚠️ Deliberately NO confirm, matching the caps forward button 40 lines above: forward
          // actions on this card state their cost in visible text, and the confirms belong to the
          // UNDOS, which strand the line. Adding one here would be the new money GATE that button's
          // comment says needs its own owner decision.
          <div>
            <Button
              variant="outline"
              size="sm"
              className="h-11 whitespace-nowrap px-3 text-xs lg:h-8 lg:px-2"
              disabled={packPending}
              onClick={() => onSetPacked(g.lineId)}
            >
              {packPending ? (
                <Loader2 className="size-3 animate-spin" />
              ) : (
                <PackageCheck className="size-3" />
              )}
              Mark packed
            </Button>
            {/* ⚠️ DERIVED, not asserted. 20 of the 21 recorded packs in prod draw nothing, because one
                ship-only product has ever been mapped — so "draws 1 from the shelf" would be false on
                almost every row this appears on. `shipOnlyPack` reads the same live mapping the draw
                itself reads. Amber only when a ledger really moves. */}
            <p
              className={cn(
                'mt-1 text-[11px] leading-snug lg:max-w-[15rem]',
                pack.movesStock ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground',
              )}
            >
              {pack.cost}
              {pack.retro &&
                ' The order has already shipped — this records the pack after the fact.'}
            </p>
          </div>
        ) : g.productionClass === 'FULFILLMENT_ONLY' && g.fulfillWorkflowState === 'PACKED' ? (
          <UndoControl
            label="Still to pack"
            title="Marked packed. Put it back on the Ship-only worklist."
            pending={undoPackedPending}
            shipped={shipped}
            onPlain={() => onUndoPacked(g.lineId)}
            onShipped={(u) => onShippedUndo(g, u)}
          />
        ) : g.workflowState === 'OPEN' &&
          g.productionClass === 'DTF_APPAREL' &&
          // Mirror the SERVER's gate exactly (review F7): it requires UNFULFILLED, and a weaker
          // client predicate would let a stale verdict render a dead-end money button that also
          // displaces the off-system mark — that line's only recording door.
          g.fulfillmentState === 'UNFULFILLED' &&
          stockMatch?.coverable ? (
          // Ship from stock (PR-4) — the whole line is pressed and sitting on the garment shelf.
          // Forward-action conventions of this card (caps/pack): NO confirm dialog (owner decision
          // D3 — a confirm is a new money gate), the cost in VISIBLE text, the undo one tap away in
          // this same zone once it lands. The server re-checks everything (whole-line coverage,
          // OPEN, book floor), so this button can never overdraw — worst case it 422s into rowError.
          <div>
            <Button
              variant="outline"
              size="sm"
              className="h-11 whitespace-nowrap px-3 text-xs lg:h-8 lg:px-2"
              disabled={shipFromStockPending}
              onClick={() => onShipFromStock(g.lineId, true)}
            >
              {shipFromStockPending ? (
                <Loader2 className="size-3 animate-spin" />
              ) : (
                <Check className="size-3" />
              )}
              Ship from stock
            </Button>
            {/* Amber, like every cost line that marks a real ledger movement on this card. */}
            <p className="mt-1 max-w-[15rem] text-[11px] leading-snug text-amber-700 dark:text-amber-400">
              Takes {stockMatch.coverQty} from the garment shelf ({stockMatch.onHand} →{' '}
              {(stockMatch.onHand ?? 0) - stockMatch.coverQty}). No film, no press — pull the shirts
              and this line is ready to ship.
              {stockMatch.artDrift
                ? ' ⚠️ The art changed since these were pressed — eyeball one first.'
                : ''}
            </p>
            {/* WHAT to grab (bin-labels plan PR-3): the printed bag label's own text + its bin id,
                from the SHELF row (the match identity) — never re-composed from the live catalog. */}
            {stockMatch.identity && (
              <p className="mt-1 max-w-[15rem] text-[11px] leading-snug text-muted-foreground">
                Bin <span className="font-mono font-semibold">{stockMatch.binId}</span> ·{' '}
                {stockIdentityLine(stockMatch.identity)}
              </p>
            )}
          </div>
        ) : canMarkOffSystem ? (
          // Forward off-system mark (PR-6) — the retro production RECORD for a line that shipped without
          // one. 259 measured lines, and this card is their only door: `/orders` reads a sparse GSI on the
          // UNFULFILLED partition, so a shipped line has no row there and never will. It is also what
          // makes the "Still needs printing" undo at the top of this chain reversible.
          //
          // ⚠️ LAST before the press fallback, deliberately. Every branch above it is a lane or a
          // lifecycle that owns the row, and `canMarkPrintedOffSystem` re-refuses each of them anyway
          // (class, then `workflowState === 'OPEN'`) — so the ordering is belt-and-braces rather than
          // load-bearing, which is the way round it should be. `!batchId` in the gate is what keeps it
          // from shadowing a live press checklist.
          //
          // ⚠️ It opens a DIALOG rather than writing, and the reason is the reason: `alreadyPrintedReason`
          // is what the row renders back, what the R1 event carries, and the whole basis for deciding
          // later whether to undo the mark. This is not the "new money GATE" the caps and pack buttons'
          // comments refuse to add — nothing is spent here at all.
          <div>
            <Button
              variant="outline"
              size="sm"
              className="h-11 whitespace-nowrap px-3 text-xs lg:h-8 lg:px-2"
              disabled={offSystemPending}
              onClick={() => onMarkOffSystem(g)}
            >
              {offSystemPending ? (
                <Loader2 className="size-3 animate-spin" />
              ) : (
                <Printer className="size-3" />
              )}
              Mark printed off-system
            </Button>
            {/* Muted, not amber: the two amber cost lines on this card mark a real ledger movement, and
                this one has none. Flattening that distinction would make the loud ones ordinary — the
                argument `shippedUndo.movesStock` already makes. */}
            <p className="mt-1 text-[11px] leading-snug text-muted-foreground lg:max-w-[15rem]">
              {OFF_SYSTEM_MARK_COST}
            </p>
          </div>
        ) : (
          <PressCell g={g} state={state} pending={pending} idle={idleNote} onPress={onPress} />
        )}
        {/* ⚠️ The failure sits WITH the control that failed. Every one of these used to render as a
            page-level line below the whole list — up to ~2,000px from the button on a long order, and
            naming no garment, so on a three-cap order "Couldn't mark it produced" sent the operator to
            guess which one. `role="alert"` because a message that appears after the tap is exactly what
            a screen reader must be told about. */}
        {rowError && (
          <p role="alert" className="mt-2 text-xs font-medium text-destructive">
            {rowError}
          </p>
        )}
      </div>
    </li>
  );
}

/** The per-garment press checklist: one check-off per placement (DTF + screen print). Check-off is
 *  enabled only while the batch is READY (in progress); a PRINTED/DRAFT batch shows read-only state. */
function PressCell({
  g,
  state,
  pending,
  idle,
  onPress,
}: {
  g: JobCardGarment;
  state?: BatchLoadState;
  pending: boolean;
  idle: React.ReactNode;
  onPress: (p: GarmentPlacement, pressed: boolean) => void;
}) {
  if (g.batchId && state === 'pending')
    return <span className="text-xs text-muted-foreground">loading…</span>;
  // ⚠️ `idle`, not an em-dash. These two conditions are most of the dead cells on a mixed order.
  if (!g.batchId || state === 'error' || g.placements.length === 0) return idle;
  const canPress = g.batchStatus === 'READY';
  return (
    <div className="flex flex-col gap-3 lg:gap-1">
      {g.placements.map((p) => (
        // A plain div (not a <label>): the shadcn Checkbox isn't a native control biome's
        // noLabelWithoutControl recognizes, and the rest of the app keys check-offs off aria-label.
        /* ⚠️ SEPARATION, not target size — and the distinction is deliberate, because an earlier
           version of this comment claimed a 44px target that does not exist. `min-h-11` is on this
           plain <div>, which has NO handler: only the Checkbox itself responds, so the real target is
           `size-6` = 24px on a phone. What the tall row buys is DEAD SPACE around a control that
           consumes a blank.
           That is the safe side of the tradeoff on purpose. Old geometry: 16px boxes in 16px rows
           `gap-1` apart = a 4px dead band, so a 10px aiming error hit the NEIGHBOURING garment. Now:
           24px boxes centred in 44px rows `gap-3` apart = a ~32px dead band, so a miss has to be
           >28px off-centre to cross over. A miss now lands on nothing (a no-op with no false success)
           instead of on the wrong garment's blank. Two adjacent 44px *targets* 4px apart would be
           WORSE than one small one — which is why the row grew and the gap grew, but the target
           only grew a little.
           ⚠️ Do NOT reach for `after:absolute after:-inset-2` (ui/sidebar.tsx:542) to enlarge the hit
           area here: that element is itself `absolute` so its pseudo anchors to its own box, whereas
           this checkbox is statically positioned and its nearest positioned ancestor is
           `SidebarInset` — the overlay would span the whole content area and funnel clicks into a
           settleLine-firing checkbox. */
        <div
          key={`${p.kind}:${p.location}`}
          className={cn(
            'flex min-h-11 items-center gap-2 text-xs lg:min-h-0',
            !canPress && 'opacity-70',
          )}
          title={
            [p.designName, p.pressedAt ? `pressed ${formatStamp(p.pressedAt)}` : null]
              .filter(Boolean)
              .join(' — ') || undefined
          }
        >
          {/* ⚠️ The accessible name names the GARMENT, not just the placement — see `pressCheckboxLabel`.
              45% of the orders with lines on a sheet were rendering two or more boxes with an identical
              name, and this is the tap that consumes a blank. */}
          <Checkbox
            aria-label={pressCheckboxLabel(g, p)}
            checked={p.pressed}
            disabled={!canPress || pending}
            onCheckedChange={(v) => onPress(p, Boolean(v))}
            className="size-6 lg:size-5"
          />
          <span className={cn(p.pressed && 'text-muted-foreground line-through')}>
            {p.location}
          </span>
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {p.kind === 'dtf' ? 'DTF' : 'screen'}
          </span>
          {/* ⚠️ Visible, not a tooltip. This is a PHYSICAL instruction — the film for this placement
              is on the shelf, not on this sheet — and it appears on no other surface in the app. A
              `title` needs hover, so on the phones the shop floor uses it could never be read. */}
          {p.fromStock && (
            <span className="rounded bg-amber-100 px-1 text-[10px] font-medium uppercase tracking-wide text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
              from shelf — not this sheet
            </span>
          )}
        </div>
      ))}
      {g.fullyPressed && (
        <p className="text-[11px] leading-snug text-muted-foreground">
          Un-ticking a placement puts this garment’s blank back on the shelf and reopens the order.
        </p>
      )}
      {g.fullyPressed && (
        <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400">
          <CheckCircle2 className="size-3.5" /> all pressed
          {/* Completion time = the LATEST placement stamp (ISO strings sort lexicographically). */}
          {(() => {
            const doneAt = g.placements
              .map((p) => p.pressedAt)
              .filter((t): t is string => Boolean(t))
              .sort()
              .at(-1);
            return doneAt ? (
              <span className="font-normal text-muted-foreground">· {formatStamp(doneAt)}</span>
            ) : null;
          })()}
        </span>
      )}
    </div>
  );
}
